/**
 * useDashboardLayout — Dashboard-Widget-Layout (F038, erweitert in Stream D / R4)
 *
 * Quelle der Wahrheit ist jetzt der Server:
 *   GET   /api/v2/dashboard/widgets  -> Katalog + gespeichertes Layout + Plan-Meta
 *   PATCH /api/v2/dashboard/layout   -> speichern (Premium-Gate, 403 premium_required)
 *
 * Frueher lag das Layout in users.dashboard_layout ueber PATCH /api/v2/account/me.
 * Der Account-Endpunkt kennt das Feld weiterhin, aber die Widget-Semantik
 * (Katalog, Default-Set, Premium-Gate) lebt in den Dashboard-Endpunkten —
 * deshalb laeuft Lesen UND Schreiben jetzt ausschliesslich ueber sie.
 *
 * Free-User (meta.customizable === false):
 *   - sehen den vollen Katalog (Upsell im Customizer),
 *   - ihre sichtbare Liste ist IMMER `effective` vom Server (= Default-Set),
 *   - lokale Toggles werden nicht gespeichert (Save ist deaktiviert).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DEFAULT_WIDGET_IDS,
  WIDGET_LABEL_KEYS,
  defaultLayoutEntries,
  migrateLayoutEntries,
  type LayoutEntry,
  type WidgetId,
} from './widgetCatalog'
import {
  DashboardLayoutError,
  useDashboardWidgetCatalog,
  useSaveDashboardLayout,
  type PlanMeta,
  type WidgetDefinition,
} from './useDashboardWidgets'

export type { WidgetId }
export { DEFAULT_WIDGET_IDS, WIDGET_LABEL_KEYS }

export type WidgetConfig = {
  id: WidgetId
  label: string
  visible: boolean
}

export type DashboardLayout = {
  widgets: WidgetConfig[]
}

const STORAGE_KEY = 'ht_dashboard_layout'

/**
 * Liest das lokal gespiegelte Layout. Alte Staende (nur die vier v2-IDs)
 * werden ueber migrateLayoutEntries tolerant auf den vollen Katalog gehoben —
 * bestehende localStorage-Eintraege gehen dadurch nicht kaputt.
 */
export function loadLayoutFromStorage(): LayoutEntry[] | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored) as { widgets?: unknown }
    const entries = migrateLayoutEntries(parsed?.widgets)
    return entries.length > 0 ? entries : null
  } catch {
    return null
  }
}

function saveLayoutToStorage(entries: LayoutEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ widgets: entries }))
  } catch {
    // Storage kann voll/deaktiviert sein — kein harter Fehler.
  }
}

export type UseDashboardLayoutOptions = {
  /** true = jede Aenderung sofort speichern (Inline-Panel), false = expliziter Save. */
  autoSave?: boolean
}

export type UseDashboardLayoutResult = {
  layout: DashboardLayout
  /** Sichtbare Widgets in Anzeigereihenfolge (Free: serverseitige effective-Liste). */
  visibleWidgets: WidgetConfig[]
  visibleIds: WidgetId[]
  catalog: WidgetDefinition[]
  meta: PlanMeta
  isLoading: boolean
  isError: boolean
  toggleWidget: (id: WidgetId) => void
  reorderWidgets: (activeId: WidgetId, overId: WidgetId) => void
  resetLayout: () => void
  /** Edit-Modus abbrechen: zurueck auf den zuletzt geladenen Serverstand. */
  revertLayout: () => void
  saveLayout: () => void
  isSaving: boolean
  saveError: DashboardLayoutError | null
  saveSucceeded: boolean
  /** true sobald lokal etwas geaendert und noch nicht gespeichert wurde. */
  isDirty: boolean
}

export function useDashboardLayout(
  options: UseDashboardLayoutOptions = {},
): UseDashboardLayoutResult {
  const { autoSave = true } = options
  const { t } = useTranslation('dashboard')
  const {
    widgets: catalog,
    layout: serverLayout,
    effective,
    meta,
    isLoading,
    isError,
  } = useDashboardWidgetCatalog()
  const mutation = useSaveDashboardLayout()

  const [entries, setEntries] = useState<LayoutEntry[]>(
    () => loadLayoutFromStorage() ?? defaultLayoutEntries(),
  )
  const [isDirty, setIsDirty] = useState(false)

  // Server-Layout uebernehmen, sobald es eintrifft — adjust-during-render statt
  // setState im Effect (react-hooks/set-state-in-effect).
  const [prevServer, setPrevServer] = useState<LayoutEntry[] | null>(null)
  if (!isLoading && serverLayout !== prevServer) {
    setPrevServer(serverLayout)
    setEntries(serverLayout)
    setIsDirty(false)
  }

  useEffect(() => {
    if (!isLoading && serverLayout.length > 0) saveLayoutToStorage(serverLayout)
  }, [isLoading, serverLayout])

  const persist = useCallback(
    (next: LayoutEntry[]) => {
      // R4-Review F6: ohne Customize-Recht darf NICHTS gespiegelt werden —
      // sonst liegt im localStorage ein Layout, das von der serverseitigen
      // `effective`-Liste abweicht (das Dashboard rendert weiter effective).
      if (meta.customizable) saveLayoutToStorage(next)
      if (autoSave && meta.customizable) {
        mutation.mutate(next)
        setIsDirty(false)
      } else {
        setIsDirty(true)
      }
    },
    [autoSave, meta.customizable, mutation],
  )

  // R4-Review F3: `persist` (PATCH + localStorage-Write) darf NICHT im
  // setState-Updater laufen — React ruft Updater unter StrictMode doppelt auf,
  // was jede Aenderung zu zwei PATCHes und zwei Storage-Writes gemacht hat.
  // Deshalb wird `next` ausserhalb berechnet, dann gesetzt, dann persistiert.
  const toggleWidget = useCallback(
    (widgetId: WidgetId) => {
      const next = entries.map((w) => (w.id === widgetId ? { ...w, visible: !w.visible } : w))
      setEntries(next)
      persist(next)
    },
    [entries, persist],
  )

  const reorderWidgets = useCallback(
    (activeId: WidgetId, overId: WidgetId) => {
      const next = [...entries]
      const oldIndex = next.findIndex((w) => w.id === activeId)
      const newIndex = next.findIndex((w) => w.id === overId)
      if (oldIndex === -1 || newIndex === -1) return
      const [removed] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, removed)
      setEntries(next)
      persist(next)
    },
    [entries, persist],
  )

  const resetLayout = useCallback(() => {
    const next = defaultLayoutEntries()
    setEntries(next)
    persist(next)
  }, [persist])

  const revertLayout = useCallback(() => {
    setEntries(prevServer ?? serverLayout)
    setIsDirty(false)
  }, [prevServer, serverLayout])

  const saveLayout = useCallback(() => {
    if (!meta.customizable) return
    mutation.mutate(entries)
    setIsDirty(false)
  }, [entries, meta.customizable, mutation])

  // Labels reaktiv aus i18n-Keys aufloesen (reagiert auf Sprachwechsel).
  const layout = useMemo<DashboardLayout>(
    () => ({
      widgets: entries.map((w) => ({
        ...w,
        label: t(WIDGET_LABEL_KEYS[w.id]),
      })),
    }),
    [entries, t],
  )

  // Free-User: der Server bestimmt die sichtbaren Kacheln (Default-Set).
  const visibleWidgets = useMemo<WidgetConfig[]>(() => {
    if (meta.customizable) return layout.widgets.filter((w) => w.visible)
    const order = effective
    return order.map((id) => ({ id, label: t(WIDGET_LABEL_KEYS[id]), visible: true }))
  }, [effective, layout, meta.customizable, t])

  const visibleIds = useMemo(() => visibleWidgets.map((w) => w.id), [visibleWidgets])

  return {
    layout,
    visibleWidgets,
    visibleIds,
    catalog,
    meta,
    isLoading,
    isError,
    toggleWidget,
    reorderWidgets,
    resetLayout,
    revertLayout,
    saveLayout,
    isSaving: mutation.isPending,
    saveError: (mutation.error as DashboardLayoutError | null) ?? null,
    saveSucceeded: mutation.isSuccess,
    isDirty,
  }
}
